/**
 * Function update the 3D-model to use the variant specified by the given name and materials.
 * 
 * Creates a new variant, if it does not already exist, with the given materials.
 */
const updateModelTextures = (variant, materials) => {
    if (!variant || !Array.isArray(materials) || materials.length === 0) {
        console.warn('Invalid variant or materials provided');
        return;
    }

    const modelViewer = document.querySelector('model-viewer');

    if (modelViewer === null) {
        console.warn('model-viewer not found');
        return;
    }

    const availableVariants = modelViewer.availableVariants ?? [];

    if (!availableVariants.includes(variant)) {
        modelViewer.model.createVariant(variant);

        materials.forEach(material => {
            if (material.includes('lining') && !material.includes('seam')) {
                if (materials.includes('goteborg')) {
                    material = material.replace(/(?<!goteborg)(_lining_)[a-z_]+/, '$1hidden');
                } else if (materials.includes('lund')) {
                    material = material.replace(/(?<!lund)(_lining_)[a-z_]+/, '$1hidden');
                } else if (materials.includes('uppsala')) {
                    material = material.replace(/(?<!uppsala)(_lining_)[a-z_]+/, '$1hidden');
                }
            }

            if (material.includes('embroidery_crown')) {
                if (materials.includes('goteborg')) {
                    material = material.replace(/(?<!goteborg)(_embroidery_crown_)[a-z_]+/, '$1hidden');
                } else if (materials.includes('lund')) {
                    material = material.replace(/(?<!lund)(_embroidery_crown_)[a-z_]+/, '$1hidden');
                } else if (materials.includes('uppsala')) {
                    material = material.replace(/(?<!uppsala)(_embroidery_crown_)[a-z_]+/, '$1hidden');
                }
            }

            const materialIndex = modelViewer.model.getMaterialByName(material)?.index;

            if (materialIndex !== undefined) {
                modelViewer.model.setMaterialToVariant(materialIndex, variant);
            }
        });
    }

    modelViewer.variantName = variant;
};

/**
 * Function to update an embroidery texture.
 * 
 * Will write the given text in the given style on the given textures.
 */
const updateEmbroidery = (text, style, textures) => {
    if (!textures?.length || textures.some(texture => texture.includes('hidden'))) {
        return;
    }

    const modelViewer = document.querySelector('model-viewer');

    let font;

    switch (style) {
        case 'Klassisk':
            font = 'MonteCarlo';
            break;
        case 'Modern':
            font = 'Times New Roman';
            break;
    }

    const getCanvasTexture = () => {
        const canvasTexture = modelViewer.createCanvasTexture();

        const canvas = canvasTexture.source.element;
        canvas.width = 1000;
        canvas.height = 1000;

        const ctx = canvas.getContext('2d');

        ctx.textBaseline = "middle";
        ctx.fillStyle = "#FFFFFF";

        if (textures.every(texture => texture.includes('firstname'))) {
            ctx.textAlign = "right";
            ctx.font = `120px ${font}`;
            ctx.translate(95, canvas.height - 40);
            ctx.rotate(Math.PI / 2);
            ctx.scale(1, -1);
        } else if (textures.every(texture => texture.includes('lastname'))) {
            ctx.textAlign = "left";
            ctx.font = `120px ${font}`;
            ctx.translate(95, 40);
            ctx.rotate(Math.PI / 2);
            ctx.scale(1, -1);
        } else if (textures.every(texture => texture.includes('band'))) {
            ctx.textAlign = "center";
            ctx.font = `55px ${font}`;
            ctx.translate(40, canvas.height / 2);
            ctx.rotate(Math.PI / 2);
            ctx.scale(1, -1);
        } else if (textures.every(texture => texture.includes('crown'))) {
            ctx.textAlign = "center";
            ctx.font = `55px ${font}`;
            ctx.translate(50, canvas.height / 2);
            ctx.rotate(Math.PI / 2);
            ctx.scale(-1, 1);
        }

        ctx.fillText(text, 0, 0);

        canvasTexture.source.update();

        return canvasTexture;
    }

    textures.forEach(async texture => {
        const material = modelViewer.model.getMaterialByName(texture);
        await material.ensureLoaded();

        if (material) {
            const pbr = material.pbrMetallicRoughness;
            const baseColor = pbr.baseColorFactor;
            baseColor[3] = 1;
            pbr.setBaseColorFactor(baseColor);
            pbr.baseColorTexture.setTexture(getCanvasTexture());
        }
    });
};

/**
 * Takes a screenshot of the current model-viewer and returns it as a JPEG data URL.
 * 
 * Uses Canvas Black Magic.
 */
const screenshotHat = () => {
    const modelViewer = document.querySelector('model-viewer');

    const srcCanvas = modelViewer.shadowRoot.querySelector('#webgl-canvas');
    const tempCanvas = document.createElement('canvas');
    const destCanvas = document.createElement('canvas');

    const tempCanvasSize = Math.min(srcCanvas.width, srcCanvas.height)

    tempCanvas.width = tempCanvasSize;
    tempCanvas.height = tempCanvasSize;

    const tempCtx = tempCanvas.getContext('2d');

    tempCtx.drawImage(srcCanvas, (tempCanvasSize - srcCanvas.width) / 2, (tempCanvasSize - srcCanvas.height) / 2);

    const imgData = tempCtx.getImageData(0, 0, tempCanvasSize, tempCanvasSize);

    // Find the bounds using the non-transparent pixels in the image data.
    let minX = tempCanvasSize, minY = tempCanvasSize, maxX = 0, maxY = 0;

    for (let i = 0; i < imgData.data.length; i += 4) {
        const alpha = imgData.data[i + 3];
        if (alpha !== 0) {
            const pixelIndex = i / 4;
            const x = pixelIndex % tempCanvasSize;
            const y = Math.floor(pixelIndex / tempCanvasSize);
            minX = Math.min(minX, x);
            minY = Math.min(minY, y);
            maxX = Math.max(maxX, x);
            maxY = Math.max(maxY, y);
        }
    }

    const destCanvasWidth = maxX - minX;
    const destCanvasHeight = maxY - minY;

    destCanvas.width = destCanvasWidth;
    destCanvas.height = destCanvasHeight;

    const destCtx = destCanvas.getContext('2d');

    destCtx.fillStyle = '#E3E3E3';
    destCtx.fillRect(0, 0, destCanvasWidth, destCanvasHeight);
    destCtx.drawImage(tempCanvas, minX, minY, destCanvasWidth, destCanvasHeight, 0, 0, destCanvasWidth, destCanvasHeight);

    return destCanvas.toDataURL('image/jpeg', 0.92);
};